
import { enqueueSnackbar } from 'notistack';
import { GraphQLErrorType, mainBackendApolloClient } from '@coverright/data-access/apollo-clients';
import {
  SaveUserProfileMutation,
  SaveUserProfileMutationVariables, useSaveUserProfileMutation
} from '../../../../../shared';
import { useSaveMedicareQuoteMutation, useSavePdpQuoteMutation } from '@coverright/data-access/medicare';
import { useSaveMedigapFilterStateMutation } from '@coverright/data-access/types/medigap';
import { useState } from 'react';
import { getAgeByBirthdate } from '@coverright/utils';
import { useClient } from '../../../hooks/client-context';
import { Gender } from '@coverright/data-access/types/enrollment';

export const useSaveProfile = (onCompleted: (data?: SaveUserProfileMutation['saveUserProfile']) => void) => {
  const client = useClient();
  const [saveMedicareQuote] = useSaveMedicareQuoteMutation({client: mainBackendApolloClient});
  const [saveMedigapFilters] = useSaveMedigapFilterStateMutation({client: mainBackendApolloClient});
  const [savePdpQuote] = useSavePdpQuoteMutation({client: mainBackendApolloClient});
  const [loading, setLoading] = useState(false)

  const [saveProfile] = useSaveUserProfileMutation({
    onError: (error) => {
      let text;
      switch (error.graphQLErrors[0]?.extensions?.type) {
        case GraphQLErrorType.ALREADY_EXISTS:
          text = 'Looks like that email is already in use.';
          break;
        case GraphQLErrorType.NO_CONNECTION:
          text = "Service is not available";
          break;
        default:
          text = "Unknown error"
      }
      enqueueSnackbar(text, {variant: "error"});
    },
    onCompleted: (data) => {
      enqueueSnackbar('User saved!', {variant: "success"});
      onCompleted(data.saveUserProfile);
    }
  })

  const save = async (values: SaveUserProfileMutationVariables['data']) => {
    setLoading(true);
    try {
      const promises: Promise<any>[] = [
        saveProfile({variables: { data: values }})
      ]


      if (client.quoteId) {
        promises.push(saveMedicareQuote({
          variables: {
            data: {
              county: values.countyName,
              zip: values.zip,
              id: client.quoteId,
            },
          },
        }))

      }

      if (client?.medigapQuote?.medigapFilterState) {
        promises.push(saveMedigapFilters({
          variables: {
            input: {
              id: client.medigapQuote.medigapFilterState.id,
              age: client.medigapQuote.medigapFilterState.age || getAgeByBirthdate(values.birthDate),
              gender: client.medigapQuote.medigapFilterState.gender as unknown as Gender || values.gender as Gender,
              tobacco: client.medigapQuote.medigapFilterState.tobacco || values.tobacco || false,
              zip: values.zip ? values.zip : client.medigapQuote.medigapFilterState.zip,
              county: values.countyName ? values.countyName : client.medigapQuote.medigapFilterState.county,
            }
          }
        }))

      }

      if (client?.pdpQuote) {
        promises.push(savePdpQuote({
          variables: {
            input: {
              medicareQuoteId: client.quoteId,
              zip: values.zip,
              countyName: values.countyName,
              id: client.pdpQuote.id,
            }
          }
        }))
      }

      await Promise.all(promises);
    } finally {
      setLoading(false);
    }
  }

  return {
    save,
    loading
  }
}
